import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getAllCompanies } from '../services/CompanyService';
import {
    deleteInvoiceAttachment, deleteOfferAttachment, getInvoiceAttachmentFile, getInvoiceAttachments,
    getOfferAttachmentFile, getOfferAttachments, previewInvoicePdf, previewOfferPdf,
    reorderInvoiceAttachments, reorderOfferAttachments, uploadInvoiceAttachment, uploadOfferAttachment,
} from '../services/InvoiceService';
import { getAllOrders, getOrderFile } from '../services/OrderService';
import { formatDate } from '../utils/dateConverter';
import { nextDocumentNumber } from '../utils/documentNumber';
import '../styles/Invoices.css';
import { useToast } from '../context/UIContext';
import { closePendingTab, openPendingTab, revealBlobInTab } from '../utils/pdfTab';
import {
    ANNEX_ACCEPT, clipboardFiles, pasteBelongsToField, pasteShortcut, prepareAnnexFiles,
} from '../utils/annexPaste';

const EMPTY_ITEM = { description: '', sub_description: '', quantity: 1, unit: 'h', unit_code: 'HUR', unit_price: 0, total: 0 };

// Resize a textarea to fit its content so multi-line descriptions stay fully visible.
const autoGrow = el => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
};

const CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF', 'JPY', 'CAD', 'AUD', 'SEK', 'NOK', 'DKK'];

const STATUS_INVOICE = ['draft', 'sent', 'paid', 'cancelled'];
const STATUS_OFFER = ['draft', 'sent', 'accepted', 'rejected'];

const STATUS_LABELS = {
    draft: 'Entwurf', sent: 'Versendet', paid: 'Bezahlt',
    cancelled: 'Storniert', accepted: 'Angenommen', rejected: 'Abgelehnt',
};

const VAT_OPTIONS = [
    { label: '0 % (steuerbefreit / Reverse Charge)', value: 0 },
    { label: '7 % (ermäßigt)', value: 7 },
    { label: '19 % (Regelsatz)', value: 19 },
];

function fmtMoney(n, currency = 'EUR') {
    return `${currency} ${Number(n || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const today = new Date().toISOString().slice(0, 10);

const fmtDay = d => (d ? formatDate(d) : '—');

const ANNEX_CALLS = {
    invoice: {
        list: getInvoiceAttachments, upload: uploadInvoiceAttachment, reorder: reorderInvoiceAttachments,
        remove: deleteInvoiceAttachment, file: getInvoiceAttachmentFile,
    },
    offer: {
        list: getOfferAttachments, upload: uploadOfferAttachment, reorder: reorderOfferAttachments,
        remove: deleteOfferAttachment, file: getOfferAttachmentFile,
    },
};

export default function InvoiceModal({ isInvoice = true, doc = null, template = null, allDocs = [], onClose, onSave }) {

    const toast = useToast();

    const [form, setForm] = useState({
        invoice_number: '',
        offer_number: '',
        recipient_name: '',
        recipient_address: '',
        recipient_vat_id: '',
        buyer_reference: '',
        order_reference: '',
        exchange_rate: 1,
        fixed_amount_eur: '',
        settled_amount_eur: '',
        settled_on: '',
        issue_date: today,
        due_date: '',
        service_period_from: '',
        service_period_to: '',
        valid_until: '',
        vat_rate: 0,
        tax_note: '',
        status: 'draft',
        rejection_reason: '',
        order_id: '',
        notes: '',
        internal_note: '',
        currency: 'EUR',
        language: 'de',
    });
    const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
    const [companies, setCompanies] = useState([]);

    useEffect(() => {
        getAllCompanies().then(setCompanies).catch(() => {});
    }, []);

    // An invoice usually bills an order. Having the order at hand while writing means
    // its period, hours and positions do not have to be looked up in another tab.
    const [orders, setOrders] = useState([]);

    useEffect(() => {
        if (!isInvoice) return;
        getAllOrders().then(o => setOrders(o || [])).catch(() => {});
    }, [isInvoice]);

    const selectedOrder = orders.find(o => String(o.id) === String(form.order_id)) || null;

    const selectableOrders = orders.filter(
        o => o.status === 'active' || String(o.id) === String(form.order_id)
    );

    /** Adopt the order: its number as the printed reference, its period as the service period. */
    const selectOrder = (orderId) => {
        const order = orders.find(o => String(o.id) === String(orderId));
        setForm(p => ({
            ...p,
            order_id: orderId,
            ...(order ? {
                order_reference: order.order_number || p.order_reference,
                service_period_from: order.start_date || p.service_period_from,
                service_period_to: order.end_date || p.service_period_to,
            } : {}),
        }));
    };

    /** Turn the order's hour positions into invoice line items. Prices stay to be filled in. */
    const adoptOrderPositions = () => {
        const lines = selectedOrder?.line_items || [];
        const source = lines.length
            ? lines.map(li => ({
                ...EMPTY_ITEM,
                description: li.description || li.ticket_name || '',
                sub_description: li.ticket_name && li.description ? li.ticket_name : '',
                quantity: parseFloat(li.hours) || 0,
            }))
            : [{ ...EMPTY_ITEM, description: selectedOrder.title || '', quantity: selectedOrder.total_hours || 0 }];
        setItems(source.map(it => ({ ...it, total: 0 })));
        toast('Positionen aus dem Auftrag übernommen — Preise noch ergänzen.');
    };

    // The order's attached document, shown in the preview pane on request.
    const [orderFileUrl, setOrderFileUrl] = useState(null);
    const [showOrderFile, setShowOrderFile] = useState(false);
    const orderFileUrlRef = useRef(null);

    useEffect(() => {
        // Selecting a different order invalidates a loaded document.
        if (orderFileUrlRef.current) {
            URL.revokeObjectURL(orderFileUrlRef.current);
            orderFileUrlRef.current = null;
        }
        setOrderFileUrl(null);
        setShowOrderFile(false);
    }, [form.order_id]);

    useEffect(() => () => {
        if (orderFileUrlRef.current) URL.revokeObjectURL(orderFileUrlRef.current);
    }, []);

    const toggleOrderFile = async () => {
        if (showOrderFile) { setShowOrderFile(false); return; }
        if (orderFileUrl) { setShowOrderFile(true); return; }
        try {
            const blob = await getOrderFile(selectedOrder.id);
            const url = URL.createObjectURL(blob);
            orderFileUrlRef.current = url;
            setOrderFileUrl(url);
            setShowOrderFile(true);
        } catch { toast('Auftragsdatei konnte nicht geladen werden.'); }
    };

    useEffect(() => {
        if (doc) {
            setForm({
                invoice_number: doc.invoice_number || '',
                offer_number: doc.offer_number || '',
                recipient_name: doc.recipient_name || '',
                recipient_address: doc.recipient_address || '',
                recipient_vat_id: doc.recipient_vat_id || '',
                buyer_reference: doc.buyer_reference || '',
                order_reference: doc.order_reference || '',
                exchange_rate: doc.exchange_rate ?? 1,
                fixed_amount_eur: doc.fixed_amount_eur ?? '',
                settled_amount_eur: doc.settled_amount_eur ?? '',
                settled_on: doc.settled_on || '',
                issue_date: doc.issue_date || today,
                due_date: doc.due_date || '',
                service_period_from: doc.service_period_from || '',
                service_period_to: doc.service_period_to || '',
                valid_until: doc.valid_until || '',
                vat_rate: doc.vat_rate ?? 0,
                tax_note: doc.tax_note || '',
                status: doc.status || 'draft',
                rejection_reason: doc.rejection_reason || '',
                order_id: doc.order_id || '',
                notes: doc.notes || '',
                internal_note: doc.internal_note || '',
                currency: doc.currency || 'EUR',
                language: doc.language || 'de',
            });
            setItems(doc.line_items?.length
                ? doc.line_items.map(it => ({ sub_description: '', ...it }))
                : [{ ...EMPTY_ITEM }]);
        }
    }, [doc]);

    // Prefilled from `template`. The number continues the highest existing one, not the template's;
    // the internal note, the settlement fields and `order_id` are deliberately not copied.
    useEffect(() => {
        if (doc || !template) return;
        const field = isInvoice ? 'invoice_number' : 'offer_number';
        setForm(p => ({
            ...p,
            [field]: nextDocumentNumber(allDocs, field),
            recipient_name: template.recipient_name || '',
            recipient_address: template.recipient_address || '',
            ...(isInvoice ? {
                recipient_vat_id: template.recipient_vat_id || '',
                buyer_reference: template.buyer_reference || '',
            } : {}),
            exchange_rate: template.exchange_rate ?? p.exchange_rate,
            vat_rate: template.vat_rate ?? p.vat_rate,
            tax_note: template.tax_note || '',
            currency: template.currency || p.currency,
            language: template.language || p.language,
            notes: template.notes || '',
        }));
        if (template.line_items?.length) {
            setItems(template.line_items.map(it => ({ sub_description: '', ...it })));
        }
    }, [template, doc, isInvoice, allDocs]);

    const setField = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

    const updateItem = (idx, field, raw) => {
        setItems(prev => {
            const next = prev.map((it, i) => {
                if (i !== idx) return it;
                const updated = { ...it, [field]: raw };
                const qty = parseFloat(updated.quantity) || 0;
                const up = parseFloat(updated.unit_price) || 0;
                return { ...updated, total: parseFloat((qty * up).toFixed(2)) };
            });
            return next;
        });
    };

    const addItem = () => setItems(p => [...p, { ...EMPTY_ITEM }]);
    const removeItem = (idx) => setItems(p => p.filter((_, i) => i !== idx));

    /** Move a position one row up or down; the PDF follows this order. */
    const moveItem = (idx, delta) => setItems(p => {
        const target = idx + delta;
        if (target < 0 || target >= p.length) return p;
        const next = [...p];
        [next[idx], next[target]] = [next[target], next[idx]];
        return next;
    });

    // Only for a saved document: an annex is stored against an id, and a draft has none yet.
    const annexesEnabled = !!doc?.id;
    const annexCalls = ANNEX_CALLS[isInvoice ? 'invoice' : 'offer'];
    const docLabel = isInvoice ? 'die Rechnung' : 'das Angebot';
    const [annexes, setAnnexes] = useState(() => doc?.attachments || []);
    const [annexBusy, setAnnexBusy] = useState(false);
    const annexInputRef = useRef(null);

    useEffect(() => {
        if (!annexesEnabled) return;
        let alive = true;
        annexCalls.list(doc.id)
            .then(list => { if (alive) setAnnexes(list || []); })
            .catch(() => {});
        return () => { alive = false; };
    }, [annexesEnabled, doc?.id, annexCalls]);

    /** Run one annex mutation, keeping the returned list as the single source of truth. */
    const runAnnexAction = async (action, okMessage) => {
        setAnnexBusy(true);
        try {
            setAnnexes(await action());
            if (okMessage) toast(okMessage);
        } catch (e) {
            toast(e?.response?.data?.detail || 'Anhang konnte nicht gespeichert werden.');
        } finally {
            setAnnexBusy(false);
        }
    };

    /** One upload after another; each answer is the full list, so a later failure keeps the earlier ones. */
    const addAnnexes = async (picked) => {
        const { files, message } = prepareAnnexFiles(picked);
        if (message) toast(message);
        if (!files.length) return;
        setAnnexBusy(true);
        let added = 0;
        try {
            for (const file of files) {
                setAnnexes(await annexCalls.upload(doc.id, file));
                added += 1;
            }
        } catch (e) {
            toast(e?.response?.data?.detail || 'Anhang konnte nicht gespeichert werden.');
        } finally {
            setAnnexBusy(false);
        }
        if (added === 1) toast('Anhang hinzugefügt — er wird dem PDF angefügt.');
        if (added > 1) toast(`${added} Anhänge hinzugefügt — sie werden dem PDF angefügt.`);
    };

    const handleAnnexPick = async (e) => {
        const picked = Array.from(e.target.files || []);
        // Cleared first: picking the same file twice must fire change again.
        e.target.value = '';
        await addAnnexes(picked);
    };

    // On the document, not the modal: after a screenshot nothing in the editor has focus.
    const pasteRef = useRef(null);
    pasteRef.current = (event) => {
        const pasted = clipboardFiles(event.clipboardData);
        if (!pasted.length || pasteBelongsToField(event)) return;
        event.preventDefault();
        if (!annexesEnabled) {
            toast(`Anhänge können eingefügt werden, sobald ${docLabel} gespeichert ist.`);
        } else if (annexBusy) {
            toast('Der vorige Anhang wird noch gespeichert — bitte gleich noch einmal einfügen.');
        } else {
            addAnnexes(pasted);
        }
    };
    useEffect(() => {
        const onPaste = (event) => pasteRef.current?.(event);
        document.addEventListener('paste', onPaste);
        return () => document.removeEventListener('paste', onPaste);
    }, []);

    const moveAnnex = (idx, delta) => {
        const target = idx + delta;
        if (target < 0 || target >= annexes.length) return;
        const order = annexes.map(a => a.id);
        [order[idx], order[target]] = [order[target], order[idx]];
        return runAnnexAction(() => annexCalls.reorder(doc.id, order));
    };

    /** Open one annex in a new tab; the tab is opened before the fetch, as in pdfTab. */
    const previewAnnex = async (annex) => {
        const tab = openPendingTab(`${annex.file_name} wird geladen…`);
        if (!tab) {
            toast('Popup wurde blockiert — bitte für diese Seite erlauben.');
            return;
        }
        try {
            revealBlobInTab(tab, await annexCalls.file(doc.id, annex.id));
        } catch {
            closePendingTab(tab);
            toast('Anhang konnte nicht geöffnet werden.');
        }
    };

    const annexPages = annexes.reduce((sum, a) => sum + (a.page_count || 0), 0);
    const annexKey = annexes.map(a => a.id).join(',');

    // Both figures are derived and display-only; only the credit is stored. The filled field is
    // the mode (fixed EUR vs. rate), so no separate flag can disagree with it.
    const fixedParsed = parseFloat(form.fixed_amount_eur);
    const fixedEur = Number.isFinite(fixedParsed) ? fixedParsed : null;
    const amountMode = form.fixed_amount_eur !== '';
    const settledParsed = parseFloat(form.settled_amount_eur);
    const settledEur = Number.isFinite(settledParsed) ? settledParsed : null;
    const totalNet = items.reduce((s, it) => s + (parseFloat(it.total) || 0), 0);
    const vat = parseFloat(form.vat_rate) || 0;
    const vatAmount = parseFloat((totalNet * vat / 100).toFixed(2));
    const totalGross = parseFloat((totalNet + vatAmount).toFixed(2));
    const expectedEur = fixedEur != null
        ? fixedEur
        : totalGross * (parseFloat(form.exchange_rate) || 1);
    const fxDiff = settledEur != null ? settledEur - expectedEur : 0;

    // Numeric line-item fields are coerced so a partial form still renders a preview.
    const buildPayload = useCallback(() => {
        const cleanItems = items.map(it => ({
            ...it,
            quantity: parseFloat(it.quantity) || 0,
            unit_price: parseFloat(it.unit_price) || 0,
            total: parseFloat(it.total) || 0,
        }));
        return {
            ...(isInvoice ? { invoice_number: form.invoice_number } : { offer_number: form.offer_number }),
            recipient_name: form.recipient_name,
            recipient_address: form.recipient_address || null,
            ...(isInvoice ? { recipient_vat_id: form.recipient_vat_id || null } : {}),
            ...(isInvoice ? { buyer_reference: form.buyer_reference || null } : {}),
            ...(isInvoice ? { order_reference: form.order_reference || null } : {}),
            ...(isInvoice ? { order_id: form.order_id || null } : {}),
            issue_date: form.issue_date,
            ...(isInvoice ? {
                due_date: form.due_date || null,
                service_period_from: form.service_period_from || null,
                service_period_to: form.service_period_to || null,
                tax_note: form.tax_note || null,
            } : {
                valid_until: form.valid_until || null,
            }),
            line_items: cleanItems,
            total_net: totalNet,
            vat_rate: vat,
            vat_amount: vatAmount,
            total_gross: totalGross,
            status: form.status,
            ...(isInvoice ? {} : {
                rejection_reason: form.status === 'rejected' ? (form.rejection_reason || null) : null,
            }),
            notes: form.notes || null,
            internal_note: form.internal_note || null,
            currency: form.currency || 'EUR',
            language: form.language || 'de',
            exchange_rate: form.currency !== 'EUR' ? (parseFloat(form.exchange_rate) || 1) : 1,
            ...(isInvoice ? {
                // Always sent, so switching back to a rate clears the fixed amount.
                fixed_amount_eur: form.currency !== 'EUR' ? fixedEur : null,
            } : {}),
            // Always sent: null is what clears a credit that was entered wrongly.
            ...(isInvoice ? {
                settled_amount_eur: form.currency !== 'EUR' ? settledEur : null,
                settled_on: form.currency !== 'EUR' ? (form.settled_on || null) : null,
            } : {}),
        };
    }, [form, items, totalNet, vat, vatAmount, totalGross, fixedEur, settledEur, isInvoice]);

    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(false);
    const previewUrlRef = useRef(null);
    const previewSeq = useRef(0);

    useEffect(() => {
        const timer = setTimeout(() => {
            const seq = ++previewSeq.current;
            setPreviewLoading(true);
            const render = isInvoice ? previewInvoicePdf : previewOfferPdf;
            render(buildPayload(), doc?.id)
                .then(blob => {
                    if (seq !== previewSeq.current) return; // a newer request superseded this one
                    const url = URL.createObjectURL(blob);
                    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
                    previewUrlRef.current = url;
                    setPreviewUrl(url);
                    setPreviewError(false);
                })
                .catch(() => { if (seq === previewSeq.current) setPreviewError(true); })
                .finally(() => { if (seq === previewSeq.current) setPreviewLoading(false); });
        }, 600);
        return () => clearTimeout(timer);
    }, [buildPayload, isInvoice, doc?.id, annexKey]);

    useEffect(() => () => {
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    }, []);

    const handleSubmit = () => {
        const docNumber = isInvoice ? form.invoice_number : form.offer_number;
        if (!docNumber || !form.recipient_name) {
            toast('Bitte Nummer und Empfänger angeben.');
            return;
        }
        for (let i = 0; i < items.length; i++) {
            if (!items[i].description?.trim()) {
                toast(`Beschreibung fehlt in Zeile ${i + 1}.`);
                return;
            }
            if (!(parseFloat(items[i].quantity) > 0)) {
                toast(`Menge muss größer als 0 sein (Zeile ${i + 1}).`);
                return;
            }
        }
        if (isInvoice && form.due_date && form.due_date < form.issue_date) {
            toast('Fälligkeitsdatum muss nach dem Ausstellungsdatum liegen.');
            return;
        }
        if (isInvoice && form.service_period_from && form.service_period_to && form.service_period_to < form.service_period_from) {
            toast('Leistungszeitraum-Ende muss nach dem Beginn liegen.');
            return;
        }
        onSave(buildPayload());
    };

    const isEdit = Boolean(doc);
    const statusOptions = isInvoice ? STATUS_INVOICE : STATUS_OFFER;
    const numberField = isInvoice ? 'invoice_number' : 'offer_number';
    const numberLabel = isInvoice ? 'Rechnungsnummer' : 'Angebotsnummer';

    return (
        <div className="modal-backdrop">
            <div className="inv-modal inv-modal--split">

                <div className="inv-modal-header">
                    <div className="inv-modal-icon">
                        <svg viewBox="0 0 24 24" fill="none">
                            <rect x="4" y="3" width="16" height="18" rx="2" stroke="currentColor" strokeWidth="1.75"/>
                            <path d="M8 8h8M8 12h6M8 16h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                    </div>
                    <div>
                        <h3 className="inv-modal-title">
                            {isEdit
                                ? (isInvoice ? 'Rechnung bearbeiten' : 'Angebot bearbeiten')
                                : (isInvoice ? 'Neue Rechnung' : 'Neues Angebot')}
                        </h3>
                        <p className="inv-modal-sub">
                            {isEdit ? form[numberField] || '—' : (isInvoice ? 'Rechnung erstellen' : 'Angebot erstellen')}
                        </p>
                    </div>
                    <button className="inv-modal-close" onClick={onClose} aria-label="Schließen">
                        <svg viewBox="0 0 20 20" fill="none">
                            <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
                        </svg>
                    </button>
                </div>

                <div className="inv-modal-split">
                <div className="inv-modal-body">

                    <div>
                        <div className="inv-section-title">Grunddaten</div>
                        <div className="inv-grid-2">
                            <div className="inv-field">
                                <label className="inv-label">{numberLabel} *</label>
                                <input className="inv-input" type="text" placeholder="z.B. 2026-001"
                                    value={form[numberField]} onChange={setField(numberField)} />
                            </div>
                            <div className="inv-field">
                                <label className="inv-label">Status</label>
                                <select className="inv-select" value={form.status} onChange={setField('status')}>
                                    {statusOptions.map(s => (
                                        <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="inv-field">
                                <label className="inv-label">Datum</label>
                                <input className="inv-input" type="date" value={form.issue_date} onChange={setField('issue_date')} />
                            </div>
                            <div className="inv-field">
                                <label className="inv-label">Sprache des PDF</label>
                                <select className="inv-select" value={form.language} onChange={setField('language')}>
                                    <option value="de">Deutsch</option>
                                    <option value="en">English</option>
                                </select>
                            </div>
                            {isInvoice ? (
                                <div className="inv-field">
                                    <label className="inv-label">Fälligkeitsdatum</label>
                                    <input className="inv-input" type="date" value={form.due_date} onChange={setField('due_date')} />
                                </div>
                            ) : (
                                <div className="inv-field">
                                    <label className="inv-label">Gültig bis</label>
                                    <input className="inv-input" type="date" value={form.valid_until} onChange={setField('valid_until')} />
                                </div>
                            )}
                            {!isInvoice && form.status === 'rejected' && (
                                <div className="inv-field full">
                                    <label className="inv-label">Ablehnungsgrund (nur intern)</label>
                                    <input className="inv-input"
                                        placeholder="z.B. Preis, Timing, Wettbewerber …"
                                        value={form.rejection_reason}
                                        onChange={setField('rejection_reason')} />
                                </div>
                            )}
                            {isInvoice && (<>
                                <div className="inv-field">
                                    <label className="inv-label">Leistungszeitraum von</label>
                                    <input className="inv-input" type="date" value={form.service_period_from} onChange={setField('service_period_from')} />
                                </div>
                                <div className="inv-field">
                                    <label className="inv-label">Leistungszeitraum bis</label>
                                    <input className="inv-input" type="date" value={form.service_period_to} onChange={setField('service_period_to')} />
                                </div>
                            </>)}
                        </div>
                    </div>

                    {isInvoice && (
                        <div>
                            <div className="inv-section-title">Auftrag</div>
                            <div className="inv-field full">
                                <label className="inv-label">Auftrag als Grundlage (optional)</label>
                                <select className="inv-select" value={form.order_id}
                                    onChange={e => selectOrder(e.target.value)}>
                                    <option value="">— Kein Auftrag —</option>
                                    {selectableOrders.map(o => (
                                        <option key={o.id} value={String(o.id)}>
                                            {o.order_number}{o.title ? ` — ${o.title}` : ''}
                                            {o.company_name ? ` (${o.company_name})` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {selectedOrder && (
                                <div className="ord-ref">
                                    <div className="ord-ref-grid">
                                        <span className="ord-ref-label">Projekt</span>
                                        <span>{selectedOrder.project_name || '—'}</span>
                                        <span className="ord-ref-label">Kunde</span>
                                        <span>{selectedOrder.company_name || '—'}</span>
                                        <span className="ord-ref-label">Zeitraum</span>
                                        <span>{`${fmtDay(selectedOrder.start_date)} – ${fmtDay(selectedOrder.end_date)}`}</span>
                                        <span className="ord-ref-label">Kontingent</span>
                                        <span>{selectedOrder.total_hours} h</span>
                                        {selectedOrder.notes && (<>
                                            <span className="ord-ref-label">Notiz</span>
                                            <span>{selectedOrder.notes}</span>
                                        </>)}
                                    </div>

                                    {selectedOrder.line_items?.length > 0 && (
                                        <table className="ord-ref-items">
                                            <tbody>
                                                {selectedOrder.line_items.map((li, i) => (
                                                    <tr key={i}>
                                                        <td>{li.description || li.ticket_name || '—'}</td>
                                                        <td className="td-amount">{li.hours} h</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}

                                    <div className="ord-ref-actions">
                                        <button type="button" className="inv-btn" onClick={adoptOrderPositions}>
                                            Positionen übernehmen
                                        </button>
                                        {selectedOrder.file_name && (
                                            <button type="button" className="inv-btn" onClick={toggleOrderFile}>
                                                {showOrderFile ? '← Zurück zur Rechnung' : `👁 ${selectedOrder.file_name}`}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div>
                        <div className="inv-section-title">Empfänger</div>
                        <div className="inv-grid-2">
                            {companies.length > 0 && (
                                <div className="inv-field full">
                                    <label className="inv-label">Aus Kundenstamm wählen</label>
                                    <select className="inv-select" defaultValue=""
                                        onChange={e => {
                                            const c = companies.find(x => x.id === e.target.value);
                                            if (!c) return;

                                            const lastForCustomer = [...allDocs]
                                                .filter(d => d.recipient_name === c.name && d.issue_date)
                                                .sort((a, b) => new Date(b.issue_date) - new Date(a.issue_date))[0];

                                            setForm(p => ({
                                                ...p,
                                                recipient_name: c.name || '',
                                                recipient_address: c.address || '',
                                                recipient_vat_id: c.vatId || '',
                                                ...(lastForCustomer ? {
                                                    vat_rate: lastForCustomer.vat_rate ?? p.vat_rate,
                                                    tax_note: lastForCustomer.tax_note || '',
                                                    currency: lastForCustomer.currency || p.currency,
                                                    language: lastForCustomer.language || p.language,
                                                    notes: lastForCustomer.notes || '',
                                                } : {}),
                                            }));

                                            if (lastForCustomer?.line_items?.length) {
                                                setItems(lastForCustomer.line_items.map(
                                                    it => ({ sub_description: '', ...it })
                                                ));
                                            }
                                        }}>
                                        <option value="">— Kunde auswählen —</option>
                                        {companies.map(c => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="inv-field full">
                                <label className="inv-label">Firmenname *</label>
                                <input className="inv-input" placeholder="Empfänger GmbH"
                                    value={form.recipient_name} onChange={setField('recipient_name')} />
                            </div>
                            <div className="inv-field full">
                                <label className="inv-label">Adresse</label>
                                <input className="inv-input" placeholder="Straße, PLZ Stadt, Land"
                                    value={form.recipient_address} onChange={setField('recipient_address')} />
                            </div>
                            {isInvoice && (
                                <div className="inv-field">
                                    <label className="inv-label">USt-IdNr. Empfänger</label>
                                    <input className="inv-input" placeholder="DE…"
                                        value={form.recipient_vat_id} onChange={setField('recipient_vat_id')} />
                                </div>
                            )}
                            {isInvoice && (
                                <div className="inv-field">
                                    <label className="inv-label">Leitweg-ID (optional)</label>
                                    <input className="inv-input" placeholder="04011000-12345-06"
                                        value={form.buyer_reference} onChange={setField('buyer_reference')} />
                                </div>
                            )}
                            {isInvoice && (
                                <div className="inv-field">
                                    <label className="inv-label">Bestell-Nr. (optional)</label>
                                    <input className="inv-input" placeholder="PO-2026-00123"
                                        value={form.order_reference} onChange={setField('order_reference')} />
                                </div>
                            )}
                        </div>
                    </div>

                    <div>
                        <div className="inv-items-header">
                            <div className="inv-section-title" style={{ marginBottom: 0 }}>Positionen</div>
                            <button className="inv-btn" onClick={addItem}>+ Position</button>
                        </div>
                        <table className="inv-items-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '5%' }}>Pos.</th>
                                    <th style={{ width: '31%' }}>Beschreibung</th>
                                    <th style={{ width: '10%' }}>Menge</th>
                                    <th style={{ width: '7%' }}>Einh.</th>
                                    <th style={{ width: '13%' }}>Einzelpreis</th>
                                    <th style={{ width: '13%', textAlign: 'right' }}>Gesamt</th>
                                    <th style={{ width: '4%' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((it, idx) => (
                                    <tr key={idx}>
                                        <td className="inv-item-order">
                                            <span className="inv-item-no">{idx + 1}</span>
                                            <div className="inv-item-move">
                                                <button
                                                    type="button"
                                                    className="inv-item-move-btn"
                                                    title="Position nach oben"
                                                    aria-label={`Position ${idx + 1} nach oben`}
                                                    onClick={() => moveItem(idx, -1)}
                                                    disabled={idx === 0}
                                                >▲</button>
                                                <button
                                                    type="button"
                                                    className="inv-item-move-btn"
                                                    title="Position nach unten"
                                                    aria-label={`Position ${idx + 1} nach unten`}
                                                    onClick={() => moveItem(idx, 1)}
                                                    disabled={idx === items.length - 1}
                                                >▼</button>
                                            </div>
                                        </td>
                                        <td>
                                            <textarea className="inv-item-input inv-item-textarea"
                                                placeholder="Beschreibung… (Enter für neue Zeile)"
                                                rows={1}
                                                ref={autoGrow}
                                                value={it.description}
                                                onInput={e => autoGrow(e.target)}
                                                onChange={e => updateItem(idx, 'description', e.target.value)} />
                                            <textarea className="inv-item-input inv-item-sub inv-item-textarea"
                                                placeholder="Unterbeschreibung (optional)…"
                                                rows={1}
                                                ref={autoGrow}
                                                value={it.sub_description || ''}
                                                onInput={e => autoGrow(e.target)}
                                                onChange={e => updateItem(idx, 'sub_description', e.target.value)} />
                                        </td>
                                        <td>
                                            <input className="inv-item-input" type="number" min="0" step="0.5"
                                                value={it.quantity}
                                                onChange={e => updateItem(idx, 'quantity', e.target.value)} />
                                        </td>
                                        <td>
                                            <input className="inv-item-input" value={it.unit}
                                                onChange={e => updateItem(idx, 'unit', e.target.value)} />
                                        </td>
                                        <td>
                                            <input className="inv-item-input" type="number" min="0" step="0.01"
                                                value={it.unit_price}
                                                onChange={e => updateItem(idx, 'unit_price', e.target.value)} />
                                        </td>
                                        <td className="inv-item-total">{fmtMoney(it.total, form.currency)}</td>
                                        <td>
                                            <button className="inv-item-del" onClick={() => removeItem(idx)} disabled={items.length === 1}>
                                                <svg viewBox="0 0 20 20" fill="none">
                                                    <path d="M6 8l1 8h6l1-8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M4 6h12M8 6V4h4v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className="inv-totals">
                            <div className="inv-totals-row">
                                <span>Nettobetrag</span>
                                <span className="inv-totals-val">{fmtMoney(totalNet, form.currency)}</span>
                            </div>
                            <div className="inv-totals-row-controls">
                                <div className="inv-field" style={{ flex: 1 }}>
                                    <label className="inv-label">Mehrwertsteuer</label>
                                    <select className="inv-select" value={form.vat_rate} onChange={setField('vat_rate')}>
                                        {VAT_OPTIONS.map(o => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="inv-field" style={{ width: '110px' }}>
                                    <label className="inv-label">Währung</label>
                                    <select className="inv-select" value={form.currency}
                                        onChange={e => {
                                            const cur = e.target.value;
                                            setForm(p => ({
                                                ...p,
                                                currency: cur,
                                                exchange_rate: cur === 'EUR' ? 1 : p.exchange_rate,
                                                fixed_amount_eur: cur === 'EUR' ? '' : p.fixed_amount_eur,
                                            }));
                                        }}>
                                        {CURRENCIES.map(c => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                </div>
                                {isInvoice && form.currency !== 'EUR' && (
                                    <div className="inv-field" style={{ width: '210px' }}>
                                        <label className="inv-label">Umrechnung</label>
                                        <div className="inv-conv">
                                            <div className="inv-conv-toggle">
                                                <button type="button"
                                                    className={`inv-conv-btn${!amountMode ? ' active' : ''}`}
                                                    onClick={() => setForm(p => ({ ...p, fixed_amount_eur: '' }))}>
                                                    Kurs
                                                </button>
                                                <button type="button"
                                                    className={`inv-conv-btn${amountMode ? ' active' : ''}`}
                                                    // Seed the field with the amount the current rate implies,
                                                    // so switching mode never blanks the EUR value.
                                                    onClick={() => setForm(p => ({
                                                        ...p,
                                                        fixed_amount_eur: p.fixed_amount_eur !== ''
                                                            ? p.fixed_amount_eur
                                                            : (totalGross * (parseFloat(p.exchange_rate) || 1)).toFixed(2),
                                                    }))}>
                                                    Fester Betrag
                                                </button>
                                            </div>
                                            {amountMode ? (
                                                <input className="inv-input" type="number" min="0" step="0.01"
                                                    placeholder="Betrag in EUR"
                                                    value={form.fixed_amount_eur}
                                                    onChange={setField('fixed_amount_eur')} />
                                            ) : (
                                                <input className="inv-input" type="number" min="0.000001" step="0.0001"
                                                    placeholder="z.B. 0.9300"
                                                    value={form.exchange_rate}
                                                    onChange={setField('exchange_rate')} />
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            {vat > 0 && (
                                <div className="inv-totals-row">
                                    <span>{vat} % MwSt.</span>
                                    <span className="inv-totals-val">{fmtMoney(vatAmount, form.currency)}</span>
                                </div>
                            )}
                            <div className="inv-totals-row total">
                                <span>Gesamtbetrag</span>
                                <span className="inv-totals-val">{fmtMoney(totalGross, form.currency)}</span>
                            </div>
                            {isInvoice && form.currency !== 'EUR' && totalGross > 0 && (
                                <div className="inv-totals-row inv-totals-row--conv">
                                    <span>
                                        {amountMode
                                            ? `Entspricht Kurs ${(expectedEur / totalGross).toFixed(6)}`
                                            : 'In EUR'}
                                    </span>
                                    <span className="inv-totals-val">{fmtMoney(expectedEur, 'EUR')}</span>
                                </div>
                            )}
                        </div>

                        {/* Bank settlement — only meaningful for a foreign-currency invoice.
                            Nothing here is printed; it corrects the EUR reporting only. */}
                        {isInvoice && form.currency !== 'EUR' && (
                            <div className="inv-settle">
                                <div className="inv-section-title" style={{ marginBottom: 0 }}>
                                    Zahlungseingang (Bank)
                                </div>
                                <div className="inv-grid-2">
                                    <div className="inv-field">
                                        <label className="inv-label">Gutschrift in EUR</label>
                                        <input className="inv-input" type="number" min="0" step="0.01"
                                            placeholder="Betrag laut Kontoauszug"
                                            value={form.settled_amount_eur}
                                            onChange={setField('settled_amount_eur')} />
                                    </div>
                                    <div className="inv-field">
                                        <label className="inv-label">Gutschrift am</label>
                                        <input className="inv-input" type="date"
                                            value={form.settled_on}
                                            onChange={setField('settled_on')} />
                                    </div>
                                </div>
                                {settledEur != null && totalGross > 0 ? (
                                    <div className="inv-settle-derived">
                                        <span>
                                            Effektiver Kurs
                                            <strong>{(settledEur / totalGross).toFixed(6)}</strong>
                                        </span>
                                        <span>
                                            {amountMode ? 'Erwartet (fester Betrag)' : `Erwartet (${form.exchange_rate || 1})`}
                                            <strong>{fmtMoney(expectedEur, 'EUR')}</strong>
                                        </span>
                                        <span className={fxDiff < 0 ? 'inv-settle-loss' : 'inv-settle-gain'}>
                                            Kursdifferenz
                                            <strong>
                                                {fxDiff < 0 ? '−' : '+'}{fmtMoney(Math.abs(fxDiff), 'EUR')}
                                            </strong>
                                        </span>
                                    </div>
                                ) : (
                                    <p className="inv-settle-hint">
                                        {amountMode
                                            ? 'Ohne Gutschrift wird mit dem festen EUR-Betrag gerechnet.'
                                            : 'Ohne Gutschrift wird mit dem Kurs der Rechnung gerechnet.'}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="inv-grid-2">
                        {isInvoice && (
                            <div className="inv-field full">
                                <label className="inv-label">Steuerhinweis (erscheint auf Rechnung)</label>
                                <input className="inv-input"
                                    placeholder="z.B. Steuerschuldnerschaft des Leistungsempfängers (Reverse Charge)"
                                    value={form.tax_note} onChange={setField('tax_note')} />
                            </div>
                        )}
                        {annexesEnabled && (
                            <div className="inv-field full inv-annexes">
                                <div className="inv-annex-head">
                                    <div className="inv-section-title" style={{ marginBottom: 0 }}>
                                        Anhänge (werden dem PDF angefügt)
                                    </div>
                                    <button
                                        type="button"
                                        className="inv-annex-add"
                                        onClick={() => annexInputRef.current?.click()}
                                        disabled={annexBusy}
                                    >{annexBusy ? 'Wird hochgeladen…' : '+ PDF oder Bild'}</button>
                                    <input
                                        ref={annexInputRef}
                                        type="file"
                                        accept={ANNEX_ACCEPT}
                                        multiple
                                        hidden
                                        data-testid="annex-input"
                                        onChange={handleAnnexPick}
                                    />
                                </div>
                                <div className="inv-annex-hint">
                                    {`Screenshot oder kopierte Datei? Einfach mit ${pasteShortcut()} einfügen.`}
                                </div>

                                {annexes.length === 0 ? (
                                    <div className="inv-annex-empty">
                                        {`Noch keine Anhänge. Ein PDF oder Bild (z.B. ${
                                            isInvoice ? 'ein Stundennachweis' : 'eine Leistungsbeschreibung'
                                        }) wird hinter ${docLabel} gehängt.`}
                                    </div>
                                ) : (
                                    <>
                                        <ul className="inv-annex-list">
                                            {annexes.map((a, idx) => (
                                                <li className="inv-annex-row" key={a.id}>
                                                    <div className="inv-item-move">
                                                        <button
                                                            type="button"
                                                            className="inv-item-move-btn"
                                                            title="Anhang nach oben"
                                                            aria-label={`${a.file_name} nach oben`}
                                                            onClick={() => moveAnnex(idx, -1)}
                                                            disabled={annexBusy || idx === 0}
                                                        >▲</button>
                                                        <button
                                                            type="button"
                                                            className="inv-item-move-btn"
                                                            title="Anhang nach unten"
                                                            aria-label={`${a.file_name} nach unten`}
                                                            onClick={() => moveAnnex(idx, 1)}
                                                            disabled={annexBusy || idx === annexes.length - 1}
                                                        >▼</button>
                                                    </div>
                                                    <span className="inv-annex-name" title={a.file_name}>
                                                        {a.file_name}
                                                    </span>
                                                    <span className="inv-annex-pages">
                                                        {a.page_count === 1 ? '1 Seite' : `${a.page_count} Seiten`}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className="inv-annex-btn"
                                                        title={`${a.file_name} in neuem Tab öffnen`}
                                                        aria-label={`${a.file_name} öffnen`}
                                                        onClick={() => previewAnnex(a)}
                                                    >👁</button>
                                                    <button
                                                        type="button"
                                                        className="inv-annex-btn inv-annex-del"
                                                        title={`${a.file_name} entfernen`}
                                                        aria-label={`${a.file_name} entfernen`}
                                                        disabled={annexBusy}
                                                        onClick={() => runAnnexAction(
                                                            () => annexCalls.remove(doc.id, a.id),
                                                            'Anhang entfernt.',
                                                        )}
                                                    >✕</button>
                                                </li>
                                            ))}
                                        </ul>
                                        <div className="inv-annex-total">
                                            {annexes.length === 1 ? '1 Anhang' : `${annexes.length} Anhänge`}
                                            {' · '}
                                            {annexPages === 1 ? '1 Seite' : `${annexPages} Seiten`}
                                            {` werden hinter ${docLabel} gehängt`}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        <div className="inv-field full">
                            <label className="inv-label">Notiz (erscheint auf dem PDF)</label>
                            <textarea className="inv-textarea" placeholder="Hinweis für den Empfänger…"
                                value={form.notes} onChange={setField('notes')} rows={2} />
                        </div>
                        <div className="inv-field full">
                            <label className="inv-label">Interner Kommentar (nur intern, nicht auf dem PDF)</label>
                            <textarea className="inv-textarea" placeholder="z.B. Warum diese Version, Absprachen, Zuständigkeit…"
                                value={form.internal_note} onChange={setField('internal_note')} rows={2} />
                        </div>
                    </div>

                </div>

                {/* Live PDF preview — or the referenced order's document */}
                <div className="inv-modal-preview">
                    {showOrderFile && orderFileUrl && (
                        <iframe
                            className="inv-preview-frame"
                            src={orderFileUrl}
                            title={`Auftragsdatei ${selectedOrder?.order_number || ''}`}
                        />
                    )}
                    {!showOrderFile && previewUrl && (
                        <iframe className="inv-preview-frame" src={previewUrl} title="PDF-Vorschau" />
                    )}
                    {!showOrderFile && !previewUrl && !previewError && (
                        <div className="inv-preview-empty">Vorschau wird geladen…</div>
                    )}
                    {!showOrderFile && previewError && !previewUrl && (
                        <div className="inv-preview-empty inv-preview-empty--error">
                            Vorschau derzeit nicht verfügbar
                        </div>
                    )}
                    {!showOrderFile && previewLoading && previewUrl && (
                        <div className="inv-preview-badge">Aktualisiere…</div>
                    )}
                </div>

                </div>

                <div className="inv-modal-footer">
                    <button className="inv-btn" onClick={onClose}>Abbrechen</button>
                    <button className="inv-btn primary" onClick={handleSubmit}>
                        {isEdit ? 'Änderungen speichern' : (isInvoice ? 'Rechnung erstellen' : 'Angebot erstellen')}
                    </button>
                </div>
            </div>
        </div>
    );
}
